/**
 * Mapea datos de widgets de escritorio (`reducers/widgets`) al contrato de `DashboardMultiSection` / cards del UI kit.
 */

import moment from "moment";

import * as i18n from "util/i18n";

/**
 * Convierte códigos de moneda del backend (numéricos ISO/Coelsa tipo 032, 840, o 0/2) en sufijo para
 * `currency.label.*`.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
function normalizeCurrencyKey(raw) {
    if (raw == null || raw === "") {
        return "ARS";
    }
    const s = String(raw).trim();
    if (s === "032" || s === "0" || s === "01" || s === "1") {
        return "ARS";
    }
    if (s === "840" || s === "2" || s === "02") {
        return "USD";
    }
    if (/^[A-Za-z]{3}$/.test(s)) {
        return s.toUpperCase();
    }
    return s;
}

/**
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
function currencyCodeToLabel(raw) {
    return i18n.get(`currency.label.${normalizeCurrencyKey(raw)}`);
}

/** Widgets configurables en el layout (excluye accounts / pending, fijos en otras secciones). */
export const MULTI_SECTION_WIDGET_IDS = ["echeqsReceived", "funds", "deposits", "financing", "metrocorp"];

export const WIDGET_ID_TO_TITLE_KEY = {
    echeqsReceived: "echeq",
    funds: "funds",
    deposits: "deposits",
    financing: "financings",
    metrocorp: "metrocorp",
};

export const TITLE_KEY_TO_WIDGET_ID = {
    echeq: "echeqsReceived",
    funds: "funds",
    deposits: "deposits",
    financings: "financing",
    metrocorp: "metrocorp",
};

const DASHBOARD_V3_WIDGET_LABEL_KEY_BY_ID = {
    deposits: "dashboard.v3.widget.deposits",
};

/**
 * @param {string} widgetId — id of the reducer/widgets (e.g. `deposits`)
 * @returns {string}
 */
export function getDashboardV3WidgetLabelKey(widgetId) {
    return DASHBOARD_V3_WIDGET_LABEL_KEY_BY_ID[widgetId] || `list.addWidget.${widgetId}`;
}

/** Icons for the empty state aligned with each card of the UI kit. */
export const EMPTY_ICON_BY_TITLE_KEY = {
    echeq: "EmitCheq",
    funds: "StocksUp",
    deposits: "StocksUp",
    financings: "StocksUp",
    metrocorp: "GraphLine",
};

/**
 * @param {Array<{ id: string, row: number }>} desktopLayout
 * @returns {{ id: string, row: number }[]} widgets multi-sección ordenados por `row` del escritorio.
 */
export function getOrderedMultiSectionLayoutEntries(desktopLayout) {
    if (!Array.isArray(desktopLayout) || desktopLayout.length === 0) {
        return [];
    }
    const set = new Set(MULTI_SECTION_WIDGET_IDS);
    return [...desktopLayout]
        .filter((w) => w && set.has(w.id))
        .sort((a, b) => (a.row ?? 0) - (b.row ?? 0))
        .map((w) => ({ id: w.id, row: w.row ?? 0 }));
}

function parseMetrocorpBalance(raw) {
    if (raw == null || raw === "") {
        return 0;
    }
    if (typeof raw === "number" && !Number.isNaN(raw)) {
        return raw;
    }
    // Strip currency symbols, letters and spaces (e.g., "$ 1.234,56" → "1.234,56")
    const s = String(raw)
        .replace(/[^0-9,.-]/g, "")
        .trim();
    if (!s) {
        return 0;
    }
    const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
    const n = parseFloat(normalized);
    return Number.isNaN(n) ? 0 : n;
}

/**
 * Suma por categoría (misma regla que MetrocorpHoldingList sin categorías dinámicas).
 *
 * El widget API (`widgets.metrocorp`) devuelve `tipoEspecie` ya traducido vía i18n del backend:
 *   - "CASH"           ← códigos raw " " / "4" / DEFAULT
 *   - "RENTA FIJA"     ← código raw "1"
 *   - "RENTA VARIABLE" ← código raw "2"
 *   - "OTRO"           ← códigos raw "0" / "3" / "5" / "8"
 * Se mantiene compatibilidad con los códigos raw por si el origen cambia.
 */
export function aggregateMetrocorpHoldings(rows) {
    let cashValue = 0;
    let fixedIncomeValue = 0;
    let variableIncomeValue = 0;
    let otherValue = 0;

    (rows || []).forEach((row) => {
        const tipo = String(row.tipoEspecie ?? "")
            .trim()
            .toUpperCase();
        const val = parseMetrocorpBalance(row.saldoValorizado);
        if (tipo === "CASH" || tipo === " " || tipo === "4") {
            cashValue += val;
        } else if (tipo === "RENTA FIJA" || tipo === "1") {
            fixedIncomeValue += val;
        } else if (tipo === "RENTA VARIABLE" || tipo === "2") {
            variableIncomeValue += val;
        } else {
            otherValue += val;
        }
    });

    return { cashValue, fixedIncomeValue, variableIncomeValue, otherValue };
}

function formatDepositAnnualRate(rate) {
    if (rate == null || rate === "") {
        return "";
    }
    const n = Number(rate);
    if (Number.isNaN(n)) {
        return String(rate);
    }
    return `${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function mapEcheqRow(row, environmentCuit, onClick) {
    const caracter = (row.cheque_caracter || "").toLowerCase();
    const toOrder = !caracter || caracter !== "no a la orden";
    const payDate = row.fecha_pago ? moment(row.fecha_pago).format("DD/MM/YYYY") : "";

    return {
        paymentDate: payDate,
        echeqNumber: String(row.cheque_numero ?? ""),
        sameHolder: Number(row.tenencia_beneficiario_documento) === Number(environmentCuit),
        toOrder,
        statusLabel: String(row.estado ?? ""),
        amount: row.monto ?? 0,
        currency: currencyCodeToLabel(row.emisor_moneda ?? "ARS"),
        onClick,
    };
}

function mapFundsRow(row, onClick) {
    const { fondo, moneda, cuotapartista, tipoValorCuotaparte, cuotapartesTotal, cuotapartesValuadas } = row;
    return {
        shareholderAccount: cuotapartista?.nombre ? String(cuotapartista.nombre) : "",
        accountNumber: cuotapartista?.numero != null ? String(cuotapartista.numero) : "",
        fundName: fondo?.nombre ? String(fondo.nombre) : "",
        fundClass: tipoValorCuotaparte?.abreviatura ? String(tipoValorCuotaparte.abreviatura) : "",
        shareCount: cuotapartesTotal ?? 0,
        amount: cuotapartesValuadas ?? 0,
        currency:
            moneda?.iso != null && moneda.iso !== "" ? currencyCodeToLabel(moneda.iso) : currencyCodeToLabel("ARS"),
        onClick,
    };
}

function mapDepositRow(row, onClick) {
    const dueRaw = row.dueDate;
    const dueDateFormatted = dueRaw ? moment(String(dueRaw).replace(".000Z", "")).format("DD/MM/YYYY") : "";

    return {
        maturityDate: dueDateFormatted,
        annualRate: formatDepositAnnualRate(row.rate),
        investedAmount: row.consolidatedAmount ?? 0,
        productName: row.productType ? String(row.productType) : "",
        certificateNumber: row.depositNumber ? String(row.depositNumber) : "",
        amountToReceive: row.nextPaymentAmount ?? 0,
        autoRenewal: Boolean(row.automaticRenewal),
        currency: currencyCodeToLabel(row.currency ?? "ARS"),
        onClick,
    };
}

function mapFinancingRow(row, onClick) {
    const { balance, currencyCode, amountPerCurrency } = row;
    const currencyLabel = i18n.get(`currency.label.${normalizeCurrencyKey(currencyCode)}`);
    const name = i18n.get(`financing.operationsType.${String(currencyLabel).toLowerCase()}`);

    return {
        activeCount: amountPerCurrency ?? 0,
        name,
        debtBalance: balance ?? 0,
        currency: currencyLabel,
        onClick,
    };
}

/**
 * Payload de `metrocorp.holdings.widgetAccounts` → contrato de `MetrocorpDashboardWidget`.
 *
 * @param {object|null} holdings
 * @param {{
 *   comitenteLabel: string,
 *   comitenteShortLabel: string,
 *   comitenteHelper: string,
 *   accountLabel: string,
 *   asOf: (date: string) => string,
 *   performanceTitle: (date: string) => string,
 * }} labels
 * @returns {object[]}
 */
/**
 * Navegación del widget hacia Tenencia. El bundle publicado a veces invoca
 * `onAccountNavigate` solo con el id; el código se recupera de la cuenta ya mapeada.
 *
 * @param {string} accountId
 * @param {string|undefined} verifyCode
 * @param {object[]} accounts
 * @returns {{ accountId: string, verifyCode?: string, speciesId: string }}
 */
export function metrocorpWidgetNavigation(accountId, verifyCode, accounts) {
    const match = (accounts || []).find((account) => String(account.id) === String(accountId));
    const resolvedVerifyCode = verifyCode || match?.verifyCode;
    return {
        accountId,
        ...(resolvedVerifyCode ? { verifyCode: resolvedVerifyCode } : {}),
        speciesId: "all",
    };
}

export function mapMetrocorpHoldingsToWidgetAccounts(holdings, labels) {
    const rows = holdings?.widgetAccounts || [];
    return rows.map((row) => {
        const comitenteNumber = String(row.comitenteNumber ?? row.id ?? "");
        const valuationDateLabel = row.valuationDateLabel || "";
        const common = {
            id: String(row.id ?? comitenteNumber),
            comitenteNumber,
            ...(row.verifyCode ? { verifyCode: row.verifyCode } : {}),
            comitenteHelperText: labels.comitenteHelper,
            isFavorite: Boolean(row.isFavorite),
        };
        if (row.hasInformation === false) {
            return { ...common, hasData: false };
        }
        return {
            ...common,
            hasData: true,
            comitenteLabel: labels.comitenteLabel,
            comitenteShortLabel: labels.comitenteShortLabel,
            patrimony: {
                accountLabel: labels.accountLabel,
                accountTag: row.patrimony?.accountTag ?? comitenteNumber,
                totalAmount: row.patrimony?.totalAmount ?? 0,
                currency: row.patrimony?.currency ?? "ARS",
                dailyPercentage: row.patrimony?.dailyPercentage,
                asOfLabel: valuationDateLabel ? labels.asOf(valuationDateLabel) : undefined,
                hideAmount: false,
            },
            performance: {
                title: valuationDateLabel ? labels.performanceTitle(valuationDateLabel) : "",
                chartData: row.performanceChartData || [],
                currency: row.patrimony?.currency ?? "ARS",
            },
        };
    });
}

/**
 * @param {object} params
 * @param {{ id: string, row: number }[]} params.layoutEntries
 * @param {object} params.widgetsState
 * @param {number} params.maxItems
 * @param {object} params.navigate
 * @param {object} [params.metrocorpWidget] — si viene, reemplaza `CardMetrocorpDashboard`.
 */
export function buildDashboardMultiSectionSections({
    layoutEntries,
    widgetsState,
    maxItems,
    navigate,
    metrocorpWidget,
}) {
    if (!layoutEntries.length) {
        return [];
    }

    const sections = [];

    layoutEntries.forEach(({ id: widgetId, row: layoutRow }) => {
        const titleKey = WIDGET_ID_TO_TITLE_KEY[widgetId];
        if (!titleKey) {
            return;
        }

        const w = widgetsState[widgetId] || {};
        const data = w.data || {};
        const loading = Boolean(w.isFetching);
        const emptyText =
            i18n.get(`desktop.widgets.${widgetId}.empty`) || i18n.get("desktop.widgets.echeqsReceived.empty");

        const widgetLabelKey = getDashboardV3WidgetLabelKey(widgetId);
        const base = {
            titleKey,
            row: layoutRow,
            toggleLabel: i18n.get(widgetLabelKey) || widgetId,
            title: i18n.get(widgetLabelKey) || widgetId,
            seeMoreKey: "dashboard.accounts.seeMore",
            seeMoreAriaKey: "dashboard.accounts.seeMoreAria",
            loading,
            iconSectionEmpty: EMPTY_ICON_BY_TITLE_KEY[titleKey],
            bgColorSectionEmpty: "neutral",
            textSectionEmpty: emptyText,
        };

        if (titleKey === "echeq") {
            const list = (data.echeqsReceived || []).slice(0, maxItems);
            const envCuit = data.environmentCuit;
            const items = list.map((row) =>
                mapEcheqRow(row, envCuit, () => navigate.pushEcheqDetail(String(row.cheque_id))),
            );
            sections.push({ ...base, titleKey: "echeq", items });
            return;
        }

        if (titleKey === "funds") {
            const list = (data.funds || []).slice(0, maxItems);
            const items = list.map((row) =>
                mapFundsRow(row, () => navigate.pushFunds(row.cuotapartista?.numero, row.fondo?.numFondo)),
            );
            sections.push({ ...base, titleKey: "funds", items });
            return;
        }

        if (titleKey === "deposits") {
            const list = (data.deposits || []).slice(0, maxItems);
            const items = list.map((row) => mapDepositRow(row, () => navigate.pushDeposit(String(row.idProduct))));
            sections.push({ ...base, titleKey: "deposits", items });
            return;
        }

        if (titleKey === "financings") {
            const list = (data.financing || []).slice(0, maxItems);
            const items = list.map((row) => mapFinancingRow(row, navigate.pushFinancing));
            sections.push({ ...base, titleKey: "financings", items });
            return;
        }

        if (titleKey === "metrocorp") {
            if (metrocorpWidget) {
                const widgetAccounts = metrocorpWidget.accounts || [];
                const emptyText = i18n.get("dashboard.cardMetrocorp.emptyValues");
                sections.push({
                    ...base,
                    titleKey: "metrocorp",
                    loading: Boolean(metrocorpWidget.loading),
                    textSectionEmpty: emptyText,
                    items: widgetAccounts.length > 0 ? [{}] : [],
                    metrocorpWidget:
                        widgetAccounts.length > 0
                            ? {
                                  accounts: widgetAccounts,
                                  emptyText,
                                  getLabels: metrocorpWidget.getLabels,
                                  onAccountNavigate: metrocorpWidget.onAccountNavigate,
                              }
                            : undefined,
                });
                return;
            }
            const rows = data.metrocorp || [];
            const { cashValue, fixedIncomeValue, variableIncomeValue, otherValue } = aggregateMetrocorpHoldings(rows);
            const accountNumber =
                data.client?.cuentas?.ctaDep?.[0]?.numeroComitente != null
                    ? String(data.client.cuentas.ctaDep[0].numeroComitente)
                    : "";

            const items =
                rows.length > 0 || accountNumber
                    ? [
                          {
                              accountNumber,
                              cashValue,
                              fixedIncomeValue,
                              variableIncomeValue,
                              otherValue,
                              currency: i18n.get("currency.label.ARS") || "$",
                              onClick: navigate.pushMetrocorp,
                          },
                      ]
                    : [];

            sections.push({ ...base, titleKey: "metrocorp", items });
        }
    });

    return sections;
}
